import { useEffect, useMemo, useRef, useState } from "react";
import { ChatBubble } from "./flowChatBubbles";
import FlowChatwootChips from "./flowChatwootChips";

const PLAY_INTERVAL_MS = 1500;

function rowKey(row) {
    return `${row.caseId}:${row.turnId}`;
}

/** Chips legíveis da evidência de rajada (debounce) no painel do replay. */
export function debounceEvidenceChips(evidence) {
    if (!evidence || typeof evidence !== "object") return [];
    const chips = [];
    const msgs = evidence.userMessagesInWindow;
    if (msgs != null && Number.isFinite(Number(msgs))) {
        chips.push({ label: "msgs enviadas", value: String(msgs) });
    }
    const gap = evidence.burstGapMs;
    if (gap != null && Number.isFinite(Number(gap))) {
        chips.push({ label: "entre msgs", value: `${Number(gap)} ms` });
    }
    const bots = evidence.botRepliesCount;
    if (bots != null && Number.isFinite(Number(bots))) {
        chips.push({ label: "respostas bot", value: String(bots) });
    }
    return chips;
}

export default function FlowChatReplay({ results = [], cases = [] }) {
    const safeResults = Array.isArray(results) ? results : [];
    const total = safeResults.length;
    // Estado final por padrão — Reset volta ao início para reassistir.
    const [revealedCount, setRevealedCount] = useState(total);
    const [selectedKey, setSelectedKey] = useState(() => {
        if (total === 0) return null;
        const last = safeResults[total - 1];
        return last?.turnId != null ? rowKey(last) : null;
    });
    const [playing, setPlaying] = useState(false);
    const playRef = useRef(null);

    const revealed = safeResults.slice(0, revealedCount);

    const caseById = useMemo(() => {
        const map = new Map();
        for (const c of cases || []) {
            if (c?.id) map.set(c.id, c);
        }
        return map;
    }, [cases]);

    const maxLatency = useMemo(() => {
        let max = 0;
        for (const row of revealed) {
            const ms = Number(row.latencyMs);
            if (Number.isFinite(ms) && ms > max) max = ms;
        }
        return max;
    }, [revealed]);

    const selectedRow = useMemo(() => {
        if (!selectedKey) return null;
        return revealed.find((r) => rowKey(r) === selectedKey) || null;
    }, [revealed, selectedKey]);

    useEffect(() => {
        if (!playing) {
            if (playRef.current) {
                clearInterval(playRef.current);
                playRef.current = null;
            }
            return undefined;
        }
        playRef.current = setInterval(() => {
            setRevealedCount((prev) => {
                if (prev >= total) {
                    setPlaying(false);
                    return prev;
                }
                const next = prev + 1;
                if (next >= total) setPlaying(false);
                return next;
            });
        }, PLAY_INTERVAL_MS);
        return () => {
            if (playRef.current) {
                clearInterval(playRef.current);
                playRef.current = null;
            }
        };
    }, [playing, total]);

    useEffect(() => {
        if (selectedKey && !revealed.some((r) => rowKey(r) === selectedKey)) {
            setSelectedKey(null);
        }
    }, [revealed, selectedKey]);

    const goNext = () => {
        setPlaying(false);
        setRevealedCount((prev) => Math.min(prev + 1, total));
    };

    const goPrev = () => {
        setPlaying(false);
        setRevealedCount((prev) => Math.max(prev - 1, 0));
    };

    const reset = () => {
        setPlaying(false);
        setRevealedCount(0);
        setSelectedKey(null);
    };

    const togglePlay = () => {
        if (playing) {
            setPlaying(false);
            return;
        }
        if (revealedCount >= total && total > 0) {
            setRevealedCount(0);
        }
        setPlaying(true);
    };

    const groups = [];
    for (const row of revealed) {
        const last = groups[groups.length - 1];
        if (!last || last.caseId !== row.caseId) {
            groups.push({ caseId: row.caseId, rows: [row] });
        } else {
            last.rows.push(row);
        }
    }

    return (
        <div className="space-y-3" aria-label="Replay chat flow">
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    aria-label="Reset replay"
                    onClick={reset}
                    className="rounded border border-gray-600 px-2 py-1 text-xs text-gray-200 hover:border-laranjinha"
                >
                    Reset
                </button>
                <button
                    type="button"
                    aria-label="Turno anterior"
                    onClick={goPrev}
                    disabled={revealedCount <= 0}
                    className="rounded border border-gray-600 px-2 py-1 text-xs text-gray-200 hover:border-laranjinha disabled:opacity-40"
                >
                    {"◀"}
                </button>
                <button
                    type="button"
                    aria-label="Próximo turno"
                    onClick={goNext}
                    disabled={revealedCount >= total}
                    className="rounded border border-gray-600 px-2 py-1 text-xs text-gray-200 hover:border-laranjinha disabled:opacity-40"
                >
                    {"▶"}
                </button>
                <button
                    type="button"
                    aria-label={playing ? "Pause replay" : "Play replay"}
                    onClick={togglePlay}
                    disabled={total === 0}
                    className="rounded border border-gray-600 px-2 py-1 text-xs text-gray-200 hover:border-laranjinha disabled:opacity-40"
                >
                    {playing ? "Pause" : "Play"}
                </button>
                <span className="text-xs text-gray-500">
                    {revealedCount}/{total} turnos
                </span>
            </div>

            {maxLatency > 0 ? (
                <div className="flex flex-wrap gap-1" aria-label="Barra de latência">
                    {revealed.map((row) => {
                        const ms = Number(row.latencyMs) || 0;
                        const pct = Math.max(8, Math.round((ms / maxLatency) * 100));
                        return (
                            <div
                                key={`lat-${rowKey(row)}`}
                                title={`${ms}ms`}
                                className={`h-1.5 rounded ${
                                    row.pass === false ? "bg-red-500/70" : "bg-green-500/70"
                                }`}
                                style={{ width: `${pct}px`, minWidth: "8%" }}
                            />
                        );
                    })}
                </div>
            ) : null}

            <div className="md:flex md:gap-4 md:items-start">
                <div className="flex-1 min-w-0">
                    {groups.length === 0 ? (
                        <p className="text-sm text-gray-400">
                            Nenhum turno revelado. Use Próximo ou Play.
                        </p>
                    ) : (
                        groups.map((group) => {
                            const meta = caseById.get(group.caseId);
                            const title = meta?.description || group.caseId || "Cenário";
                            return (
                                <div key={group.caseId} className="mb-4">
                                    <h5 className="text-xs font-medium text-laranjinha mb-2">
                                        {title}
                                    </h5>
                                    {group.rows.map((row) => {
                                        const key = rowKey(row);
                                        const selected = selectedKey === key;
                                        const select = () => setSelectedKey(key);
                                        return (
                                            <div key={key}>
                                                <ChatBubble
                                                    role="user"
                                                    text={row.input}
                                                    selected={selected}
                                                    onClick={select}
                                                    testId={`replay-user-${row.turnId}`}
                                                />
                                                <ChatBubble
                                                    role="bot"
                                                    text={row.output}
                                                    pass={row.pass}
                                                    selected={selected}
                                                    onClick={select}
                                                    testId={`replay-bot-${row.turnId}`}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })
                    )}
                </div>

                {selectedRow ? (
                    <aside
                        className="mt-3 md:mt-0 md:w-80 shrink-0 rounded border border-gray-700 bg-gray-800/50 p-3 space-y-2 text-xs text-gray-300"
                        aria-label="Detalhe do turno"
                    >
                        <p>
                            <span className="text-gray-500">input:</span> {selectedRow.input || "—"}
                        </p>
                        <div>
                            <span className="text-gray-500">output:</span>
                            <pre className="mt-1 whitespace-pre-wrap break-words text-gray-400 bg-gray-950/50 p-2 rounded">
                                {selectedRow.output || "—"}
                            </pre>
                        </div>
                        {selectedRow.latencyMs != null ? (
                            <p>
                                <span className="text-gray-500">latência:</span>{" "}
                                {selectedRow.latencyMs}ms
                            </p>
                        ) : null}
                        {selectedRow.intent ? (
                            <p>
                                <span className="text-gray-500">intent:</span> {selectedRow.intent}
                            </p>
                        ) : null}
                        {selectedRow.handoff_now != null ? (
                            <p>
                                <span className="text-gray-500">handoff_now:</span>{" "}
                                {String(selectedRow.handoff_now)}
                            </p>
                        ) : null}
                        {selectedRow.reason ? (
                            <p>
                                <span className="text-gray-500">reason:</span> {selectedRow.reason}
                            </p>
                        ) : null}
                        {selectedRow.debounceEvidence ? (
                            <div data-testid="replay-debounce-evidence">
                                <p className="text-gray-500">rajada (debounce)</p>
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                    {debounceEvidenceChips(selectedRow.debounceEvidence).map(
                                        (chip) => (
                                            <div
                                                key={chip.label}
                                                className="rounded border border-gray-600/70 bg-gray-900/70 px-2 py-1"
                                                data-testid={`replay-debounce-${chip.label.replace(/\s+/g, "-")}`}
                                            >
                                                <p className="text-[9px] uppercase tracking-wide text-gray-500">
                                                    {chip.label}
                                                </p>
                                                <p className="text-xs text-gray-200">{chip.value}</p>
                                            </div>
                                        )
                                    )}
                                </div>
                                <details className="mt-1">
                                    <summary className="cursor-pointer text-gray-500 hover:text-gray-300">
                                        JSON completo
                                    </summary>
                                    <pre className="mt-1 whitespace-pre-wrap break-words text-gray-500 bg-gray-950/50 p-2 rounded">
                                        {JSON.stringify(selectedRow.debounceEvidence, null, 2)}
                                    </pre>
                                </details>
                            </div>
                        ) : null}
                        {selectedRow.chatwootSnapshot ? (
                            <>
                                <FlowChatwootChips snapshot={selectedRow.chatwootSnapshot} />
                                <details>
                                    <summary className="cursor-pointer text-gray-400 hover:text-gray-200">
                                        Chatwoot snapshot
                                    </summary>
                                    <pre className="mt-1 whitespace-pre-wrap break-words text-gray-500 bg-gray-950/50 p-2 rounded">
                                        {JSON.stringify(selectedRow.chatwootSnapshot, null, 2)}
                                    </pre>
                                </details>
                            </>
                        ) : null}
                    </aside>
                ) : null}
            </div>
        </div>
    );
}
